import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, inject, input } from '@angular/core';

import PerfectScrollbar from 'perfect-scrollbar';

export interface PerfectScrollbarConfig {
  handlers?: string[];
  wheelSpeed?: number;
  wheelPropagation?: boolean;
  swipeEasing?: boolean;
  minScrollbarLength?: number;
  maxScrollbarLength?: number;
  scrollingThreshold?: number;
  suppressScrollX?: boolean;
  suppressScrollY?: boolean;
  scrollXMarginOffset?: number;
  scrollYMarginOffset?: number;
}

const DEFAULT_CONFIG: PerfectScrollbarConfig = {
  suppressScrollX: true,
  wheelPropagation: false
};

/**
 * Lightweight replacement for the abandoned `ngx-perfect-scrollbar` wrapper.
 * Wraps the framework-agnostic `perfect-scrollbar` library and keeps the same
 * `[perfectScrollbar]` selector plus the handful of helper methods the app used
 * (`update`, `scrollToElement`, `position`, `scrollToTop`).
 */
@Directive({
    selector: '[perfectScrollbar]',
    exportAs: 'ngxPerfectScrollbar'
})
export class PerfectScrollbarDirective implements AfterViewInit, OnDestroy {
  private _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private _zone = inject(NgZone);

  readonly configInput = input<PerfectScrollbarConfig | ''>('', { alias: "perfectScrollbar" });
  readonly config = input<PerfectScrollbarConfig | undefined>(undefined);
  readonly disabled = input(false);

  private _ps: PerfectScrollbar | null = null;

  ngAfterViewInit(): void {
    if (this.disabled()) {
      return;
    }
    const configInput = this.configInput();
    const config: PerfectScrollbarConfig = {
      ...DEFAULT_CONFIG,
      ...(typeof configInput === 'object' ? configInput : {}),
      ...(this.config() || {})
    };
    this._zone.runOutsideAngular(() => {
      this._ps = new PerfectScrollbar(this._elementRef.nativeElement, config);
    });
  }

  ngOnDestroy(): void {
    this._zone.runOutsideAngular(() => {
      this._ps?.destroy();
      this._ps = null;
    });
  }

  /** Recalculate the scrollbar geometry (call after the content size changes). */
  update(): void {
    this._zone.runOutsideAngular(() => this._ps?.update());
  }

  /** Current scroll offset of the container. */
  position(_absolute?: boolean): { x: number; y: number } {
    const el = this._elementRef.nativeElement;
    return { x: el.scrollLeft, y: el.scrollTop };
  }

  /** Scroll a descendant matching `selector` into view, with an optional pixel `offset`. */
  scrollToElement(selector: string, offset = 0, _speed?: number): void {
    const container = this._elementRef.nativeElement;
    const target = container.querySelector<HTMLElement>(selector);
    if (!target) {
      return;
    }
    const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop + offset;
    container.scrollTop = top;
    this.update();
  }

  scrollToTop(offset = 0, _speed?: number): void {
    this._elementRef.nativeElement.scrollTop = offset;
    this.update();
  }

  scrollToBottom(offset = 0, _speed?: number): void {
    const el = this._elementRef.nativeElement;
    el.scrollTop = el.scrollHeight - el.clientHeight - offset;
    this.update();
  }
}
